import { jsPDF } from "jspdf";
import { pdfText } from "@/lib/pdfText";
import { deliverExport } from "@/components/drill/nativeExport";

import { safeLocalStorage } from '@/lib/browserStorage';
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const HEADINGS = ["Bits", "Casing", "Consumables", "Muds", "Oils", "Rig spares", "subs"].map(
  capitalize
);

function subsDetail(subs) {
  if (!Array.isArray(subs) || subs.length === 0) return "";
  const a = subs[0] || {};
  const b = subs[1] || {};
  const parts = [];
  if (a.description) parts.push(a.description);
  if (a.selection) parts.push(`[${a.selection}]`);
  if (b.description) parts.push(b.description);
  if (b.selection) parts.push(`[${b.selection}]`);
  return parts.join(" ").trim();
}

function bitsDetail(bits) {
  if (!bits) return "";
  return [
    bits.thread && `Thread: ${bits.thread}`,
    bits.size && `Size: ${bits.size}`,
    bits.type && `Type: ${bits.type}`,
  ].filter(Boolean).join(" · ");
}

function casingDetail(casing) {
  if (!casing) return "";
  return [
    casing.length && `Length: ${casing.length}`,
    casing.type && `Type: ${casing.type}`,
    casing.size && `Size: ${casing.size}`,
  ].filter(Boolean).join(" · ");
}

function flattenItems(items) {
  const rows = [];
  for (const heading of HEADINGS) {
    const list = items?.[heading] || [];
    list.forEach((item) => {
      const isSubs = heading === "Subs";
      const fallback = typeof item === "string" ? item : item.name;
      let name = fallback;
      if (isSubs) name = subsDetail(item && item.subs) || fallback;
      const obj = typeof item === "string" ? {} : item;
      if (heading === "Bits" && bitsDetail(obj.bits)) {
        name = name ? `${name} (${bitsDetail(obj.bits)})` : bitsDetail(obj.bits);
      }
      if (heading === "Casing" && casingDetail(obj.casing)) {
        name = name ? `${name} (${casingDetail(obj.casing)})` : casingDetail(obj.casing);
      }
      const qty = typeof item === "string" ? 1 : item.quantity ?? 0;
      const buildTo = typeof item === "string" ? 0 : item.build_to || 0;
      const recOrder = Math.max(0, buildTo - (qty || 0));
      const uom = typeof item === "string" ? "each" : (item.uom || "each");
      rows.push({ heading, name, uom, qty, buildTo, recOrder });
    });
  }
  return rows;
}

function safeName(rigId) {
  return `stocktake_${(rigId || "rig").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
}

async function triggerDownload(blob, filename) {
  await deliverExport(blob, filename);
}

export async function exportStocktakeCsv(rigId, items) {
  const rows = flattenItems(items);
  const header = ["Category", "Item", "UOM", "Quantity", "Build to", "Recommended order"];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    lines.push([esc(r.heading), esc(r.name), esc(r.uom || "each"), r.qty, r.buildTo, r.recOrder].join(","));
  });
  const csv = lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  await triggerDownload(blob, `${safeName(rigId)}.csv`);
}

export async function exportStocktakePdf(rigId, items) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const companyName = safeLocalStorage.getItem("drilllog_company_name") || "";
  const companyLogo = safeLocalStorage.getItem("drilllog_company_logo") || "";
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  if (companyLogo) { try { doc.addImage(companyLogo, ((companyLogo.match(/^data:image\/(\w+)/)||[])[1] || "png").toUpperCase() === "JPG" ? "JPEG" : ((companyLogo.match(/^data:image\/(\w+)/)||[])[1] || "png").toUpperCase(), pageW - margin - 90, y - 12, 90, 28, undefined, "FAST"); } catch {} }
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(companyName ? `${companyName} — Stocktake Report` : "Stocktake Report", margin, y);
  y += 20;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(pdfText(`Rig ID: ${rigId || "—"}`), margin, y);
  y += 14;
  doc.text(
    pdfText(`Generated: ${new Date().toLocaleString()}`),
    margin,
    y
  );
  y += 24;

  // Table header
  const cols = [margin, margin + 130, margin + 300, margin + 390, margin + 480, margin + 560];
  doc.setFillColor(15, 23, 42);
  doc.rect(margin, y - 14, pageW - margin * 2, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Category", cols[0] + 6, y);
  doc.text("Item", cols[1] + 6, y);
  doc.text("UOM", cols[2] + 6, y);
  doc.text("Qty", cols[3] + 6, y);
  doc.text("Build to", cols[4] + 6, y);
  doc.text("Rec. order", cols[5] + 6, y);
  y += 10;
  doc.setTextColor(0, 0, 0);

  const rows = flattenItems(items);
  doc.setFont("helvetica", "normal");
  rows.forEach((r, i) => {
    if (y > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      y = margin;
    }
    if (i % 2 === 0) {
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, y - 12, pageW - margin * 2, 20, "F");
    }
    doc.text(pdfText(r.heading), cols[0] + 6, y);
    doc.text(pdfText(r.name), cols[1] + 6, y);
    doc.text(pdfText(r.uom), cols[2] + 6, y);
    doc.text(pdfText(r.qty), cols[3] + 6, y);
    doc.text(pdfText(r.buildTo), cols[4] + 6, y);
    doc.text(pdfText(r.recOrder), cols[5] + 6, y);
    y += 20;
  });

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Total items: ${rows.length}`,
    margin,
    y + 10
  );

  const blob = doc.output("blob");
  await triggerDownload(blob, `${safeName(rigId)}.pdf`);
}